import { HPIInfoModal } from "@/components/optimization/hpi-info-modal";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/radix/tooltip";
import { useHousePricingStore } from "@/stores/house-pricing-store";
import React, { useCallback, useState } from "react";

import currencify from "@/utils/currency/format";
import { formatPricePerSqft } from "@/utils/currency/get-rate";
import { formatRange } from "@/utils/format-range";
import { type ComparablePrice } from "../optimization/types";

// ---------------------------------------
// 1. FORMAT DATA HOOK
// ---------------------------------------

interface UseFormattedPropertyData {
	prices: number[];
	currentRate?: number;
	builtUpAreas: number[];
	parkings: number[];
	comparablePrices?: ComparablePrice[];
	plotAreas: number[];
	distance?: number;
}
const useFormattedPropertyData = ({
	prices,
	currentRate,
	builtUpAreas,
	parkings,
	comparablePrices,
	plotAreas,
	distance,
}: UseFormattedPropertyData) => {
	// Format distance - show in m or km with space
	const distanceStr = distance
		? distance >= 1000
			? `${(distance / 1000).toFixed(1)} km`
			: `${Math.round(distance)} m`
		: "0 m";

	const priceNum = prices?.[0];
	const priceStr = priceNum && priceNum > 0 ? currencify({ amount: priceNum, abbreviated: true }) : "";

	const rateStr = (
		currentRate
			? Math.round(currentRate)
			: formatPricePerSqft({
					price: priceNum || 0,
					area: builtUpAreas?.[0] || 0,
				}) || ""
	).toLocaleString();

	const estimatedPriceToday = comparablePrices?.[0]?.estimatedPriceToday || 0;

	const estimatedPriceTodayStr =
		estimatedPriceToday > 0 ? currencify({ amount: estimatedPriceToday, abbreviated: true }) : "";

	const estimatedPriceTodayPerSqft =
		estimatedPriceToday > 0
			? (formatPricePerSqft({
					price: estimatedPriceToday,
					area: builtUpAreas?.[0] || 0,
				}) as string)
			: "";

	const parkingsStr = formatRange({ data: parkings });
	const builtUpAreasStr = formatRange({
		data: builtUpAreas,
		formatter: v => v.toLocaleString(),
	});

	const plotAreaStr = (plotAreas?.[0] || "").toLocaleString();
	return {
		priceStr,
		rateStr,
		estimatedPriceTodayStr,
		estimatedPriceTodayPerSqft,
		parkingsStr,
		builtUpAreasStr,
		plotAreaStr,
		distanceStr,
	};
};

// ---------------------------------------
// 2. DYNAMIC LAYOUT BUILDER
// ---------------------------------------

interface LayoutBlock {
	type: "primary" | "secondary" | "meta";
	value: string;
	prefixLabel?: string;
	suffixLabel?: string;
	underline?: boolean;
	tooltip?: string;
	button?: boolean;
	onClick?: () => void;
}

interface LayoutResult {
	left: LayoutBlock[];
	right: LayoutBlock[];
}

interface BuildLayout {
	supType?: string;
	forWhat: string;
	priceStr: string;
	rateStr: string;
	estimatedPriceTodayStr: string;
	estimatedPriceTodayPerSqft: string;
	parkingsStr: string;
	builtUpAreasStr: string;
	parkings: number[];
	propertyTypes: string[];
	constructionRate?: number;
	plotRate?: number;
	distanceStr: string;
}
interface BuildLayoutParams extends BuildLayout {
	onEstimatedPriceClick?: () => void;
}

const buildLayout = ({
	supType,
	forWhat,
	priceStr,
	rateStr,
	estimatedPriceTodayStr,
	estimatedPriceTodayPerSqft,
	parkingsStr,
	builtUpAreasStr,
	parkings,
	propertyTypes,
	constructionRate,
	plotRate,
	distanceStr,
	onEstimatedPriceClick,
}: BuildLayoutParams): LayoutResult => {
	const isHouse = propertyTypes?.some(pt => pt.toLowerCase() === "house");
	const priceSuffix = supType === "sp" ? "+" : "";

	const layout: LayoutResult = {
		left: [],
		right: [],
	};

	// SOLD & CANCELLED
	if (forWhat === "sold_and_cancelled") {
		if (isHouse) {
			// LEFT SIDE (META INFO)

			// Display Plot Rate from store if available
			if (plotRate) {
				layout.left.push({
					type: "primary" as const,
					value: `₹${plotRate.toLocaleString()}`,
					prefixLabel: "Plot Rate",
				});
			}

			// Display Construction Rate from store if available
			if (constructionRate) {
				layout.left.push({
					type: "secondary" as const,
					value: `₹${constructionRate.toLocaleString()}`,
					prefixLabel: "Construction Rate",
				});
			}

			if (estimatedPriceTodayPerSqft) {
				layout.right.push({
					type: "secondary" as const,
					value: estimatedPriceTodayPerSqft,
					suffixLabel: "/sqft",
					tooltip: "Estimated rate per sqft",
				});
			}

			if (estimatedPriceTodayStr) {
				layout.right.push({
					type: "secondary" as const,
					value: `₹${estimatedPriceTodayStr} ${priceSuffix} (estd.)`,
					underline: true,
					tooltip: `Estimated price today: ${estimatedPriceTodayStr} (as per HPI)`,
					onClick: onEstimatedPriceClick,
				});
			}
		}

		// DEFAULT
		else {
			// LEFT SIDE (META INFO)
			if (estimatedPriceTodayPerSqft) {
				layout.left.push({
					type: "primary" as const,
					value: estimatedPriceTodayPerSqft,
					suffixLabel: "sft",
				});
			}

			if (estimatedPriceTodayStr) {
				layout.left.push({
					type: "secondary" as const,
					value: `₹${estimatedPriceTodayStr} ${priceSuffix} (estd.)`,
					underline: true,
					tooltip: `Estimated price today: ${estimatedPriceTodayStr} (as per HPI)`,
					onClick: onEstimatedPriceClick,
				});
			}

			// RIGHT SIDE (META INFO)
			if (distanceStr) {
				layout.right.push({
					type: "meta" as const,
					value: distanceStr,
					tooltip: "Distance from your property",
				});
			}
			if (rateStr) {
				layout.right.push({
					type: "secondary" as const,
					value: rateStr,
					suffixLabel: "sft",
				});
			}
		}
	}

	// LIVE
	else if (forWhat === "live") {
		if (isHouse) {
			// LEFT SIDE (META INFO)

			// Display Plot Rate from store if available
			if (plotRate) {
				layout.left.push({
					type: "primary" as const,
					value: `₹${plotRate.toLocaleString()}`,
					prefixLabel: "Plot Rate",
				});
			}

			// Display Construction Rate from store if available
			if (constructionRate) {
				layout.left.push({
					type: "secondary" as const,
					value: `₹${constructionRate.toLocaleString()}`,
					prefixLabel: "Construction Rate",
				});
			}

			// RIGHT SIDE (META INFO)
			if (rateStr) {
				layout.right.push({
					type: "primary" as const,
					value: rateStr,
					tooltip: "Rate per sqft",
					suffixLabel: "/sqft",
				});
			}

			if (priceStr) {
				layout.right.push({
					type: "secondary" as const,
					value: `₹${priceStr}`,
				});
			}
		}

		// DEFAULT
		else {
			// LEFT SIDE (META INFO)
			if (rateStr) {
				layout.left.push({
					type: "primary" as const,
					value: rateStr,
					suffixLabel: "/sqft",
				});
			}

			if (priceStr) {
				layout.left.push({
					type: "secondary" as const,
					value: `₹${priceStr}`,
				});
			}

			// RIGHT SIDE (META INFO)
			if (distanceStr) {
				layout.right.push({
					type: "meta" as const,
					value: distanceStr,
					tooltip: "Distance from your property",
				});
			}
			if (builtUpAreasStr) {
				layout.right.push({
					type: "secondary" as const,
					value: builtUpAreasStr,
					suffixLabel: "/sqft",
				});
			}
		}
	}

	// DEFAULT
	else {
		// LEFT SIDE (META INFO)
		if (priceStr) {
			layout.left.push({
				type: "primary" as const,
				value: `₹${priceStr} ${priceSuffix}`,
			});
		}

		if (rateStr) {
			layout.left.push({
				type: "secondary" as const,
				value: rateStr,
				suffixLabel: "/sqft",
			});
		}

		// RIGHT SIDE (META INFO)
		if (parkingsStr) {
			layout.right.push({
				type: "meta" as const,
				value: parkingsStr,
				suffixLabel: `parking${Math.max(...parkings) > 1 ? "s" : ""}`,
			});
		}

		if (builtUpAreasStr) {
			layout.right.push({
				type: "meta" as const,
				value: builtUpAreasStr,
				suffixLabel: "Sqft",
			});
		}
	}

	return layout;
};

// ---------------------------------------
// 3. GENERIC BLOCK RENDERER
// ---------------------------------------

interface RenderBlockProps {
	block: LayoutBlock;
}
const RenderBlock = ({ block }: RenderBlockProps) => {
	if (!block?.value) return null;

	// Get base styling based on type
	const getTypeStyles = () => {
		switch (block.type) {
			case "primary":
				return "text-lg font-semibold";
			case "secondary":
				return "text-sm opacity-90";
			case "meta":
				return "text-sm";
			default:
				return "";
		}
	};

	// Build additional classes
	const additionalClasses = [
		block.underline && "underline decoration-dotted underline-offset-2",
		(block.onClick || block.button) && "cursor-pointer hover:opacity-100",
		block.tooltip && !block.onClick && !block.button && "cursor-help",
		// block.align === "right" && "text-right",
	]
		.filter(Boolean)
		.join(" ");

	const className = `${getTypeStyles()} ${additionalClasses}`.trim();

	// Build content
	const content = (
		<div className={className} onClick={block.onClick}>
			{block.prefixLabel} {block.value}
			{block.suffixLabel ? ` ${block.suffixLabel}` : null}
		</div>
	);

	// Wrap with tooltip if provided
	if (block.tooltip) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<span>{content}</span>
				</TooltipTrigger>
				<TooltipContent>
					<p className="text-sm">{block.tooltip}</p>
				</TooltipContent>
			</Tooltip>
		);
	}

	return content;
};

// ---------------------------------------
// 4. MAIN COMPONENT
// ---------------------------------------

interface PropertyInfoOverlayProps {
	forWhat: string;
	supType?: string;
	prices: number[];
	currentRate?: number;
	builtUpAreas: number[];
	parkings: number[];
	comparablePrices?: ComparablePrice[];
	propertyTypes: string[];
	plotAreas: number[];
	supId: string;
	distance?: number;
}
export const PropertyInfoOverlay = React.memo(function PropertyInfoOverlay({
	forWhat,
	supType,
	prices,
	currentRate,
	builtUpAreas,
	parkings,
	comparablePrices,
	propertyTypes,
	plotAreas,
	supId,
	distance,
}: PropertyInfoOverlayProps) {
	const [showHPIModal, setShowHPIModal] = useState(false);

	// Get pricing data from store for real-time updates
	// Use a selector that tracks the specific property's data
	const pricingData = useHousePricingStore(state => state.pricingData[supId]);

	const formatted = useFormattedPropertyData({
		prices,
		currentRate,
		builtUpAreas,
		parkings,
		comparablePrices,
		plotAreas,
		distance,
	});

	const handleEstimatedPriceClick = useCallback(() => {
		setShowHPIModal(true);
	}, []);

	const layout = buildLayout({
		propertyTypes,
		supType,
		forWhat,
		parkings,
		...formatted,
		constructionRate: pricingData?.constructionRate,
		plotRate: pricingData?.plotRate,
		onEstimatedPriceClick: handleEstimatedPriceClick,
	});

	return (
		<>
			<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent text-white p-4 pt-8">
				<div className="flex items-end justify-between">
					{/* LEFT SIDE */}
					<div className="flex flex-col justify-end space-y-1">
						{layout.left.map((block, i) => (
							<RenderBlock key={i} block={block} />
						))}
					</div>

					{/* RIGHT SIDE - Fill from bottom, maintain order */}
					<div className="flex flex-col justify-end space-y-1 text-right">
						{layout.right.map((block, i) => (
							<RenderBlock key={i} block={block} />
						))}
					</div>
				</div>
			</div>

			{/* MODAL */}
			{forWhat === "sold_and_cancelled" && (
				<HPIInfoModal
					isOpen={showHPIModal}
					comparablePrices={comparablePrices}
					onClose={() => setShowHPIModal(false)}
				/>
			)}
		</>
	);
});
